import React, { useEffect, useMemo, useState } from 'react';
import { Checkbox, Grid, Input, Select } from 'semantic-ui-react';
import useFetchTable from '../../../hooks/useFetchTable';
import FiltersComponent from '../../../components/FiltersComponent';
import CustomTable from '../../../components/CustomTable/CustomTable';
import TableStats from '../../../components/TableStats';
import SendNotificationModal from '../../../components/SendNotificationModal';
import { RangeInput } from '../../../components/FiltersComponent/FilterField';
import { BooleanCell, StatusCell } from '../../../components/CustomCell';
import UserCard from '../../Cards/UserCard';
import { url } from '../../../shared/URL';
import {
  taxStatusOptions,
  userStatusOptions,
  userRoleOptions,
} from '../../../shared/constants';
import { fetchLoyaltyCategories } from '../../Motivation/api';
import { defaultPageRequest } from '../../Motivation/types';
import {
  loyaltyCategoryLabel,
  resolveLoyaltyMedal,
} from '../../../utils/loyaltyMedal';

const SellersPage = () => {
  const fetchTable = useFetchTable(url.getSellers);
  const [categoryById, setCategoryById] = useState<{
    [key: number]: string;
  }>({});

  useEffect(() => {
    fetchLoyaltyCategories({ ...defaultPageRequest, size: 200 })
      .then(({ items }) => {
        const map: { [key: number]: string } = {};
        (items || []).forEach((c) => {
          if (c.id !== undefined && c.id !== null) {
            map[c.id] = c.name;
          }
        });
        setCategoryById(map);
      })
      .catch(() => {});
  }, []);

  const columns = useMemo(
    () => [
      { Header: 'ФИО пользователя', accessor: 'surnameWithInitials' },
      { Header: 'Номер телефона', accessor: 'mobile' },
      {
        Header: 'Категория',
        id: 'loyaltyCategory',
        accessor: (row: any) => {
          const direct = loyaltyCategoryLabel(row);
          if (direct && Number.isNaN(Number(direct))) {
            return direct;
          }
          const nestedId =
            row.loyaltyCategory &&
            typeof row.loyaltyCategory === 'object' &&
            row.loyaltyCategory.id !== undefined &&
            row.loyaltyCategory.id !== null
              ? Number(row.loyaltyCategory.id)
              : null;
          const id =
            row.loyaltyCategoryId !== undefined && row.loyaltyCategoryId !== null
              ? Number(row.loyaltyCategoryId)
              : nestedId !== null
              ? nestedId
              : direct && !Number.isNaN(Number(direct))
              ? Number(direct)
              : null;
          if (id !== null && categoryById[id]) {
            return categoryById[id];
          }
          return direct || '';
        },
        Cell: ({ value }: { value: string }) => {
          if (!value) {
            return <span>—</span>;
          }
          const medal = resolveLoyaltyMedal(value);
          return (
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
              }}
            >
              {medal ? (
                <img
                  src={medal.src}
                  alt={medal.label}
                  title={medal.label}
                  width={22}
                  height={22}
                  style={{ objectFit: 'contain' }}
                />
              ) : null}
              <span>{value}</span>
            </span>
          );
        },
        disableSortBy: true,
      },
      {
        Header: 'Статус ФНС',
        accessor: 'taxStatus',
        Cell: (row) => StatusCell(row, taxStatusOptions),
      },
      {
        Header: 'Роль продавца',
        accessor: 'status',
        Cell: (row) => StatusCell(row, userStatusOptions),
      },
      {
        Header: 'Статус продавца',
        accessor: 'roles',
        Cell: (row) => row && StatusCell(row, userRoleOptions),
      },
      { Header: 'ИНН организации', accessor: 'pointInn' },
      { Header: 'Продажи', accessor: 'salesAmount' },
      {
        Header: 'Блокировка пользователя',
        accessor: 'locked',
        Cell: (row) => BooleanCell(row),
      },
      {
        Header: 'Блокировка вывода баллов',
        accessor: 'taxLocked',
        Cell: (row) => BooleanCell(row),
      },
      {
        Header: 'Баллы за продажи',
        accessor: 'chargedBonus',
        // K-01: API chargedBonus excludes loyalty upgrade / first-sale / missions.
        // Renamed until backend returns full accrual sum.
      },
      { Header: 'Выведенные баллы', accessor: 'dischargedBonus' },
    ],
    [categoryById]
  );

  return (
    <div className="page table-page">
      <h1 className="page__header">Список продавцов</h1>
      <Grid stackable>
        <Grid.Row>
          <Grid.Column className="table-page__stats">
            <TableStats totalElements={fetchTable.totalElements} />
          </Grid.Column>
        </Grid.Row>
        <Grid.Row>
          <Grid.Column width={3}>
            <FiltersComponent
              applyFilters={fetchTable._apply}
              totalElements={fetchTable.totalElements}
              render={SendNotificationModal}
            >
              <Input
                type="text"
                name="surnameWithInitials"
                placeholder="ФИО пользователя"
              />
              <Input type="number" name="mobile" placeholder="Номер телефона" />
              <Select
                placeholder="Статус ФНС"
                options={taxStatusOptions}
                name="taxStatus"
                clearable
                multiple
              />
              <Select
                placeholder="Роль продавца"
                options={userRoleOptions}
                name="roles"
                clearable
                multiple
              />
              <Select
                placeholder="Статус пользователя"
                options={userStatusOptions}
                defaultValue={'SELLER'}
                disabled
                name="status"
                clearable
                multiple
              />
              <RangeInput
                type="range"
                name="salesAmount"
                placeholder="Количество продаж"
              />
              <Checkbox name="locked" label="Пользователь заблокирован" />
              <Checkbox name="taxLocked" label="Вывод баллов заблокирован" />
            </FiltersComponent>
          </Grid.Column>
          <Grid.Column width={13}>
            <CustomTable
              table={fetchTable}
              columns={columns}
              CardComponent={UserCard}
            />
          </Grid.Column>
        </Grid.Row>
      </Grid>
    </div>
  );
};

export default SellersPage;
